import { Close, Psychology, Send } from "@mui/icons-material";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import { useEffect, useState } from "react";
import SearchableSenderProfileSelect, {
  loadAllSenderProfiles,
} from "../../components/SearchableSenderProfileSelect";
import { hostname, parseError } from "../../utils";

export default ({
  open,
  onClose,
  selectedInbox,
  getToken,
  eventListener,
  companyId,
  onEmailSent,
  onManageProfiles,
}: {
  open: boolean;
  onClose: () => void;
  selectedInbox: any;
  getToken: Function;
  eventListener: Function;
  companyId: string;
  onEmailSent?: () => void;
  onManageProfiles?: () => void;
}) => {
  const [loading, setLoading] = useState(false);
  const [profilesLoading, setProfilesLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [sending, setSending] = useState(false);

  // Form state
  const [to, setTo] = useState("");
  const [subject, setSubject] = useState("");
  const [content, setContent] = useState("");
  const [selectedProfile, setSelectedProfile] = useState("");
  const [selectedIntegration, setSelectedIntegration] = useState("");

  // Options from API
  const [profiles, setProfiles] = useState<Array<any>>([]);
  const [integrations, setIntegrations] = useState<Array<any>>([]);

  const handleClose = () => {
    setTo("");
    setSubject("");
    setContent("");
    setSelectedProfile("");
    setSelectedIntegration("");
    onClose();
  };

  const loadProfiles = async () => {
    setProfilesLoading(true);
    const currentToken = await getToken();
    try {
      const allProfiles = await loadAllSenderProfiles(currentToken, companyId);
      setProfiles(allProfiles);
    } catch (err) {
      console.error("Error loading profiles:", err);
    } finally {
      setProfilesLoading(false);
    }
  };

  const loadIntegrations = async () => {
    const currentToken = await getToken();
    try {
      const res = await axios.get(`${hostname}/integrations/by-type/email`, {
        params: { companyId },
        headers: {
          Authorization: `Bearer ${currentToken}`,
        },
      });
      setIntegrations(res.data.items || []);
    } catch (err) {
      console.error("Error loading integrations:", err);
    }
  };

  const generateAIContent = async () => {
    if (!subject.trim()) {
      eventListener({
        type: "SET_ERROR",
        error: "Please enter a subject line before generating AI content.",
      });
      return;
    }

    setGenerating(true);
    try {
      const currentToken = await getToken();
      const res = await axios.post(
        `${hostname}/inbox/compose/generate`,
        {
          to: to.trim(),
          subject: subject.trim(),
          companyId,
        },
        {
          headers: {
            Authorization: `Bearer ${currentToken}`,
          },
        }
      );

      if (res.data.content) {
        setContent(res.data.content);
      }
    } catch (err) {
      console.error("Error generating AI content:", err);
      eventListener({
        type: "SET_ERROR",
        error: parseError(err),
      });
    } finally {
      setGenerating(false);
    }
  };

  const sendEmail = async () => {
    if (!to.trim() || !subject.trim() || !content.trim()) {
      eventListener({
        type: "SET_ERROR",
        error: "Please fill in all required fields (To, Subject, Content).",
      });
      return;
    }

    if (!selectedIntegration) {
      eventListener({
        type: "SET_ERROR",
        error: "Please select an email integration.",
      });
      return;
    }

    setSending(true);
    try {
      const currentToken = await getToken();
      await axios.post(
        `${hostname}/inbox/${selectedInbox?.id}/compose`,
        {
          to: to.trim(),
          subject: subject.trim(),
          content: content.trim(),
          ...(selectedProfile && { profileId: selectedProfile }),
          integrationId: selectedIntegration,
          companyId,
        },
        {
          headers: {
            Authorization: `Bearer ${currentToken}`,
          },
        }
      );

      eventListener({
        type: "SET_SUCCESS",
        message: "Email sent successfully!",
      });

      handleClose();
      onEmailSent?.();
    } catch (err) {
      console.error("Error sending email:", err);
      eventListener({
        type: "SET_ERROR",
        error: parseError(err),
      });
    } finally {
      setSending(false);
    }
  };

  // Load data when dialog opens
  useEffect(() => {
    if (open) {
      setLoading(true);
      Promise.all([loadProfiles(), loadIntegrations()]).finally(() => {
        setLoading(false);
      });
    }
  }, [open]);

  // Auto-select profile matching the selected inbox
  useEffect(() => {
    if (profiles.length > 0 && !selectedProfile && selectedInbox?.id) {
      // Find a profile that matches this inbox
      const matchingProfile = profiles.find(
        (p: any) => p.inboxId === selectedInbox.id
      );
      if (matchingProfile) {
        setSelectedProfile(matchingProfile.id);
      } else if (profiles.length === 1) {
        // Fallback to first if only one available
        setSelectedProfile(profiles[0].id);
      }
    }
  }, [profiles, selectedProfile, selectedInbox]);

  // Auto-select integration matching the selected inbox or profile
  useEffect(() => {
    if (integrations.length > 0 && !selectedIntegration) {
      // First try to match by inbox's integrationId
      if (selectedInbox?.integrationId) {
        const matchingIntegration = integrations.find(
          (i: any) => i.id === selectedInbox.integrationId
        );
        if (matchingIntegration) {
          setSelectedIntegration(matchingIntegration.id);
          return;
        }
      }
      // Then try matching by selected profile's integrationId
      if (selectedProfile) {
        const profile = profiles.find((p: any) => p.id === selectedProfile);
        if (profile?.integrationId) {
          const matchingIntegration = integrations.find(
            (i: any) => i.id === profile.integrationId
          );
          if (matchingIntegration) {
            setSelectedIntegration(matchingIntegration.id);
            return;
          }
        }
      }
      // Fallback to first if only one available
      if (integrations.length === 1) {
        setSelectedIntegration(integrations[0].id);
      }
    }
  }, [integrations, selectedIntegration, selectedInbox, selectedProfile, profiles]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6">
            Compose New Email
            {selectedInbox && ` - ${selectedInbox.name}`}
          </Typography>
          <IconButton onClick={handleClose}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {loading && <LinearProgress sx={{ mb: 2 }} />}

        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {/* Recipient Field */}
          <TextField
            label="To"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            fullWidth
            required
            placeholder="recipient@example.com"
            type="email"
          />

          {/* Subject Field */}
          <TextField
            label="Subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            fullWidth
            required
          />

          {/* Sender Profile */}
          <SearchableSenderProfileSelect
            profiles={profiles}
            selectedId={selectedProfile}
            onChangeSingle={(id) => setSelectedProfile(id)}
            label="Sender Profile (Optional)"
            helperText="Select a sender profile to use their signature and details."
            onManageProfiles={onManageProfiles}
            loading={profilesLoading}
          />

          {/* Email Integration */}
          <FormControl fullWidth required>
            <InputLabel>Email Service</InputLabel>
            <Select
              value={selectedIntegration}
              onChange={(e) => setSelectedIntegration(e.target.value)}
              label="Email Service"
            >
              {integrations.map((integration: any) => (
                <MenuItem key={integration.id} value={integration.id}>
                  {integration.name} - {integration.type}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Content Field with AI Generation */}
          <Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1,
              }}
            >
              <Typography variant="subtitle2">Email Content</Typography>
              <Button
                startIcon={<Psychology />}
                onClick={generateAIContent}
                disabled={generating || !subject.trim()}
                size="small"
                variant="outlined"
              >
                {generating ? "Generating..." : "Generate with AI"}
              </Button>
            </Box>
            <TextField
              multiline
              rows={8}
              value={content}
              onChange={(e) => setContent(e.target.value)}
              fullWidth
              required
              placeholder="Enter your email content here..."
            />
          </Box>

          {/* Action Buttons */}
          <Box
            sx={{ display: "flex", justifyContent: "flex-end", gap: 2, pt: 2 }}
          >
            <Button onClick={handleClose} disabled={sending}>
              Cancel
            </Button>
            <Button
              variant="contained"
              startIcon={<Send />}
              onClick={sendEmail}
              disabled={
                sending || !to.trim() || !subject.trim() || !content.trim()
              }
            >
              {sending ? "Sending..." : "Send Email"}
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};
